/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

/* eslint-disable max-len */
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { getValidationsThunk } from '../submission/submission.thunk.validations';
import { saveSubmissionAndRedirect, creatEpctRedirect } from './dialog.thunk';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';

/* Initial State */
const initialState = {
  config: {
    dialogProcedure: undefined,
    uiSchemaProps: undefined,
    validationSchemaProps: undefined,
  },

  isOpen: false,
  isLoading: false,

  contents: {
    submission: undefined,
    uiSchema: undefined,
    validationErrors: [],
    isValid: false,
  },
};

// /**
//  * support validations for dialogs configurations for pending
//  * @param action
//  * @returns {boolean}
//  */
// const getDialogValidationPendingMatcher = action =>
//   [getValidationsThunk.pending.type].includes(action.type) &&
//   action.payload?.uiSchema?.[uiSchemaProperties.DIALOG_ID];

/**
 * support validations for dialogs configurations for fullfilled
 * @param action
 * @returns {boolean}
 */
const getDialogValidationFulfilledMatcher = (action) => {
  // Convert the thunk action type to its string representation
  const expectedTypeString = getValidationsThunk.fulfilled.toString();

  const typeMatches = action.type === expectedTypeString;
  const uiSchemaMatches = action.payload?.uiSchema?.[uiSchemaProperties.DIALOG_ID] !== undefined;

  return typeMatches && uiSchemaMatches;
};

/**
 * support validations for dialogs configurations for rejected
 * @param action
 * @returns {boolean}
 */
const getDialogValidationRejectedMatcher = (action) => {
  // Convert the thunk action type to its string representation for comparison
  const expectedRejectedTypeString = getValidationsThunk.rejected.toString();

  // Check if the action type matches the expected rejected type
  // and if the uiSchema with the specific DIALOG_ID exists in the payload
  return (
    action.type === expectedRejectedTypeString && action.payload?.uiSchema?.[uiSchemaProperties.DIALOG_ID] !== undefined
  );
};

/* Slice */
export const dialogSlice = createSlice({
  name: 'dialogs',
  initialState,
  reducers: {
    openDialog(state, { payload: { dialogProcedure, uiSchemaProps, validationSchemaProps, submission } }) {
      state.config.dialogProcedure = dialogProcedure;
      state.config.uiSchemaProps = uiSchemaProps;
      state.config.validationSchemaProps = validationSchemaProps;
      state.contents.submission = submission;
      state.isOpen = true;
    },
    closeDialog(state) {
      Object.assign(state, initialState);
    },
    updateSubmission(state, action) {
      state.contents.submission = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(saveSubmissionAndRedirect.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveSubmissionAndRedirect.fulfilled, (state) => {
        Object.assign(state, initialState);
      })
      .addCase(saveSubmissionAndRedirect.rejected, (state) => {
        Object.assign(state, initialState);
      })
      .addCase(creatEpctRedirect.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(creatEpctRedirect.fulfilled, (state) => {
        Object.assign(state, initialState);
      })
      .addCase(creatEpctRedirect.rejected, (state) => {
        Object.assign(state, initialState);
      })
      .addMatcher(getDialogValidationFulfilledMatcher, (state, action) => {
        state.contents.submission = action.payload?.submission;
        state.contents.uiSchema = action.payload?.uiSchema;
        state.contents.validationErrors = action.payload?.validationErrors ?? initialState.contents.validationErrors;
        state.contents.isValid = !!action.payload?.workflowValidations?.isValid;
      })
      .addMatcher(getDialogValidationRejectedMatcher, (state) => {
        state.contents.isValid = false;
      });
  },
});

/* Selectors */
const selectDialog = state => state[dialogSlice.name];

export const selectDialogConfig = createSelector(selectDialog, ({ config }) => config);

export const selectDialogIsOpen = createSelector(selectDialog, ({ isOpen }) => isOpen);
export const selectDialogIsLoading = createSelector(selectDialog, ({ isLoading }) => isLoading);

export const selectDialogContents = createSelector(
  selectDialog,
  ({ contents: { submission, uiSchema, isValid, validationErrors } }) => ({
    submission,
    uiSchema,
    isValid: isValid && validationErrors.length === 0,
  }),
);

/* Actions */
export const { openDialog, closeDialog, updateSubmission } = dialogSlice.actions;
